/*
 * PRD-037 — Capa de datos del paso de plan del claim (app SEPARADA del panel).
 *
 * El portal del partner NO monta Redux ni el AuthContext del panel, así que los hooks del panel
 * (useAvailableSubscriptions, useActivatePlan) NO funcionan aquí: usan useSelector / useAuth y
 * crashearían sin sus providers. En su lugar replicamos sus DOS llamadas contra los MISMOS
 * endpoints usando axiosInstance directo: su interceptor de request inyecta el JWT de comercio +
 * el header id_comercio leyéndolos de localStorage (ver axiosInstance.ts) — justo lo que persiste
 * ClaimPage tras el claim. Así el authorizer del gateway recibe los headers que espera, sin
 * arrastrar los providers del panel ni romper el patrón de app separada.
 *
 * Reusamos los CONTRATOS de tipo del panel (import type, cero runtime) para no duplicar la forma:
 * - AvailableSubscription: lo que devuelve GET /v1/planes.
 * - ActivatePlanResponse / MetodoPago: el contrato de convertFreemium (FR-28).
 */
import axiosInstance from '@services/axiosInstance';
import type { AvailableSubscription } from '@interfaces/general';
import {
  normalizeActivateResponse,
  type ActivatePlanResponse,
  type MetodoPago,
  type RawActivatePlanResponse,
} from '@pages/Subscriptionsv2/hooks/useActivatePlan';

// GET /v1/planes — mismos planes públicos (is_public) que muestra el panel (useAvailableSubscriptions).
export const fetchPublicPlans = async (): Promise<AvailableSubscription[]> => {
  const { data } = await axiosInstance.get<AvailableSubscription[]>('/v1/planes');
  return data;
};

// Cuerpo del POST a convertFreemium. `coupon` es opcional: SOLO se envía si el dueño tecleó un
// código de descuento. El campo lleva el STRING tal cual (contrato acordado con el backend, que lo
// resuelve vía promotion_codes.list y lo aplica; si no existe, el pago sigue a precio lleno). El FE
// NO valida el código.
interface ConvertFreemiumRequest {
  plan_id: string;
  metodo_pago: MetodoPago;
  coupon?: string;
}

// POST /v1/suscriptions/convertFreemium — el dueño recién reclamado es FREEMIUM, así que SIEMPRE
// es la rama convertFreemium de useActivatePlan (nunca createSubscription). El backend responde
// un envoltorio de tracking de steps { message, completed_steps, data: { client_secret |
// hosted_invoice_url } } — normalizamos con la MISMA función que useActivatePlan (no duplicar
// el unwrap) para no repetir el bug de leer client_secret en el nivel equivocado.
export const convertFreemiumToPlan = async (
  planId: string,
  metodoPago: MetodoPago,
  coupon?: string,
): Promise<ActivatePlanResponse> => {
  const body: ConvertFreemiumRequest = {
    plan_id: planId,
    metodo_pago: metodoPago,
  };
  // Solo incluir el campo cuando el usuario escribió algo (tras trim). Si va vacío, no se manda.
  const trimmedCoupon = coupon?.trim();
  if (trimmedCoupon) body.coupon = trimmedCoupon;

  const { data } = await axiosInstance.post<RawActivatePlanResponse>('/v1/suscriptions/convertFreemium', body);
  return normalizeActivateResponse(data);
};
